import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, isAxiosError } from 'axios';
import posthog from 'posthog-js';

import { setPotentiallyAuthenticated } from 'services/auth/auth';
import { ENV } from 'services/environment/environment';
import { SessionStorageService } from 'services/sessionStorage/sessionStorage';
import { readCookie } from 'support/helpers/cookies/cookies';
import {
  BackendError,
  BadCallError,
  ConflictError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
  ValidationError,
} from 'support/helpers/errors/errors';

export type ValidationErrors = Record<string, Array<string>>;

type GenericErrorResponse = {
  message: string;
  code?: string;
  context?: Record<string, unknown>;
};

type ValidationErrorResponse = GenericErrorResponse & {
  errors: ValidationErrors;
};

type ErrorResponseType = ValidationErrorResponse | GenericErrorResponse;

let csrfCookieRefreshPromise: Promise<AxiosResponse> | null = null;

// Fallback for the CSRF token when the 'XSRF-TOKEN' cookie is inaccessible.
let inMemoryToken: string | null = null;
export const BASE_URL = ENV.API_BASE_URL ?? '/';

const instance = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
  headers: {
    Accept: 'application/json',
  },
  withCredentials: true,
});

// Handle CSRF token
instance.interceptors.request.use(async (config) => {
  if (config.method === 'post' || config.method === 'put' || config.method === 'patch' || config.method === 'delete') {
    const csrfToken = await getCsrfToken();

    if (config.baseURL === BASE_URL) {
      config.headers['X-Xsrf-Token'] = csrfToken;
    }
  }

  return config;
});

// Handle error responses
instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Try fetching new csrf token if the current one is expired
    if (error.response?.status === 419) {
      const newCsrfToken = await fetchNewCsrfToken();
      // Ensure in-memory token is updated if cookie is still not readable
      if (newCsrfToken && !readCookie('XSRF-TOKEN')) {
        inMemoryToken = newCsrfToken;
      }
      if (!newCsrfToken && !inMemoryToken) {
        // Check both cookie and memory
        return Promise.reject(error);
      }
      const newConfig = { ...error.response?.config } as AxiosRequestConfig;
      if (!newConfig.headers) {
        newConfig.headers = {};
      }

      // Set the new CSRF token
      newConfig.headers['X-Xsrf-Token'] = newCsrfToken || inMemoryToken;
      return axios(newConfig);
    }

    // Logout user if the token is invalid
    if (error.response?.status === 401) {
      setPotentiallyAuthenticated(false);
      SessionStorageService.clear();
    }

    // Handle other errors
    return Promise.reject(error);
  },
);

// Handle API request tracking
instance.interceptors.response.use(
  (response) => {
    // Track all non-get requests
    if (response.config.method !== 'get') {
      posthog.capture('portal_api_call', {
        status: response.status,
        url: response.config.url,
        method: response.config.method,
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    if (!!error.response && !!error.config && error.response?.status !== 401 && error.response?.status !== 419) {
      posthog.capture('portal_api_call', {
        status: error.response.status,
        url: error.config.url,
        method: error.config.method,
      });
    }

    // Handle other errors
    return Promise.reject(error);
  },
);

export const axiosInstance = instance;

export const fetchData = async <ResponseType>(
  url: string,
  options?: { method?: 'get' | 'post' | 'put' | 'patch' | 'delete'; data?: any; config?: AxiosRequestConfig },
) => {
  try {
    const response = await instance.request({
      url,
      method: options?.method || 'get',
      ...(options?.data &&
        ['post', 'put', 'patch', 'delete'].includes(options?.method || 'get') && { data: options.data }),
      ...(options?.config && options.config),
    });

    // A status of 0 means no HTTP response arrived (aborted / network / CORS failure); axios's
    // `settle` resolves these as success, which would cache an empty body as valid data. Reject
    // it. Strictly `=== 0` — a real response always carries a numeric status — to match
    // isStatusCodeZeroError.
    if (response.status === 0) {
      throw new BackendError('Request failed: no response received (status 0)', 0);
    }

    return {
      status: response.status,
      data: response.data as ResponseType,
      headers: response.headers,
    };
  } catch (error) {
    if (!isAxiosError<ErrorResponseType>(error)) {
      throw error;
    }

    if (!error.response) {
      throw error;
    }

    throw mapResponseError(error);
  }
};

export const download = async (url: string) => fetchData<Blob>(url, { config: { responseType: 'blob' } });

export const addHostToUrl = (url: string) => {
  if (url.startsWith('http')) {
    return url;
  }

  return `${window.location.protocol}//${window.location.host}${url}`;
};

export const getGoogleDriveDownloadUrl = (url: string): string | null => {
  const match = url.match(/\/file\/d\/([^/]+)/);
  if (!match) {
    return null;
  }
  return `https://drive.usercontent.google.com/download?id=${match[1]}`;
};

/**
 * Hands the URL to the browser to fetch, without navigating the current page or opening a tab —
 * for URLs that already carry their own authentication, such as presigned S3 links.
 *
 * The `download` attribute is honoured for same-origin and `blob:` URLs only; for a cross-origin
 * URL the browser ignores it and relies on the server sending `Content-Disposition: attachment`.
 *
 * To download a protected app/API path, use {@link downloadFile} instead.
 */
export const triggerBrowserDownload = (url: string, filename?: string) => {
  const link = document.createElement('a');

  link.setAttribute('href', url);
  if (filename) {
    link.setAttribute('download', filename);
  }
  link.setAttribute('rel', 'noopener');
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);

  triggerBrowserDownload(url, filename);

  window.URL.revokeObjectURL(url);
};

/**
 * Fetches the file ourselves through the authenticated axios instance, then saves it — for
 * protected app/API paths the browser cannot request on its own.
 *
 * For a URL that already carries its own authentication (a presigned S3 link), use
 * {@link triggerBrowserDownload} instead: our auth headers would break the signature.
 */
export const downloadFile = async (downloadUrl: string, options?: { contentType?: string; filename?: string }) => {
  const { data } = await download(downloadUrl);
  const blob = data.slice(0, data.size, options?.contentType);
  saveBlob(blob, options?.filename ?? (downloadUrl.split('/').pop() || 'download'));
};

export const createData = async <ResponseType, BodyType = any>(url: string, data?: BodyType) =>
  fetchData<ResponseType>(url, {
    method: 'post',
    data,
  });

export const updateData = async <ResponseType>(url: string, data?: any) =>
  fetchData<ResponseType>(url, {
    method: 'put',
    data,
  });

export const patchData = async <ResponseType>(url: string, data?: any) =>
  fetchData<ResponseType>(url, {
    method: 'patch',
    data,
  });

export const removeData = async <ResponseType>(url: string, data?: any) =>
  fetchData<ResponseType>(url, {
    method: 'delete',
    data,
  });

const isValidationErrorResponse = (response: ErrorResponseType): response is ValidationErrorResponse => {
  return (response as ValidationErrorResponse).errors !== undefined;
};

const fetchNewCsrfToken = async () => {
  // Start new CSRF token request if not already running
  if (!csrfCookieRefreshPromise) {
    csrfCookieRefreshPromise = instance.get('/sanctum/csrf-cookie');
  }

  // Await running CSRF token request
  try {
    await csrfCookieRefreshPromise;
  } finally {
    // Once done, reset promise placeholder
    csrfCookieRefreshPromise = null;
  }
  const token = readCookie('XSRF-TOKEN');
  return token || inMemoryToken;
};

const getCsrfToken = async () => {
  let csrfToken = readCookie('XSRF-TOKEN');
  if (!csrfToken && inMemoryToken) {
    csrfToken = inMemoryToken;
  }
  if (!csrfToken) {
    csrfToken = await fetchNewCsrfToken();
  }

  return csrfToken;
};

const errorMessageOr = (message: string | undefined, fallback: string): string => message || fallback;

const mapResponseError = (error: AxiosError<ErrorResponseType>): Error => {
  const response = error.response;
  if (!response) {
    return error;
  }

  const responseMessage = response.data?.message;
  switch (response.status) {
    case 400:
      return new BadCallError(errorMessageOr(responseMessage, 'Bad Call'));
    case 401:
      return new UnauthorizedError(errorMessageOr(responseMessage, 'Unauthorised'));
    case 403:
      return new ForbiddenError(errorMessageOr(responseMessage, 'Forbidden'));
    case 404:
      return new NotFoundError(errorMessageOr(responseMessage, 'Not Found'));
    case 409:
      return new ConflictError(errorMessageOr(responseMessage, 'Conflict'));
    case 422: {
      const errors = isValidationErrorResponse(response.data) ? response.data.errors : {};
      return new ValidationError(errorMessageOr(responseMessage, 'Validation Error'), errors, {
        code: response.data?.code,
        context: response.data?.context,
      });
    }
    default:
      return new BackendError(errorMessageOr(responseMessage, 'Unknown Error'), response.status);
  }
};
